import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useBooking, scrollToId } from "@/booking";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Who I Work With", id: "audiences" },
  { label: "Services", id: "services" },
  { label: "Values", id: "values" },
  { label: "FAQ", id: "faq" },
];

export const Nav = () => {
  const openBooking = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header data-testid="nav-header" className="fixed inset-x-0 top-0 z-50 px-4 pt-4">
      <div
        className={`mx-auto flex max-w-6xl items-center justify-between rounded-full border px-4 py-2.5 pl-3 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled
            ? "border-peach/25 bg-cream/85 shadow-[0_12px_40px_-16px_rgba(226,121,111,0.4)] backdrop-blur-xl"
            : "border-transparent bg-transparent"
        }`}
      >
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative font-heading text-[13px] font-semibold text-ink-muted transition-colors duration-300 hover:text-ink"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-peach-dark transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            data-testid="nav-book-call-btn"
            onClick={openBooking}
            className="group hidden items-center gap-1.5 rounded-full bg-peach-dark px-5 py-2.5 font-heading text-[13px] font-bold text-cream shadow-[0_10px_30px_-10px_rgba(226,121,111,0.7)] transition-[background-color,transform] duration-300 hover:bg-peach-deeper hover:-translate-y-0.5 sm:flex"
          >
            Book Strategy Call — ₹199
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-peach/30 bg-cream/80 text-ink lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-2 flex max-w-6xl flex-col gap-1 rounded-3xl border border-peach/25 bg-cream/95 p-4 shadow-xl backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="rounded-xl px-4 py-3 text-left font-heading text-sm font-semibold text-ink transition-colors hover:bg-peach-soft"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-book-call-btn"
              onClick={() => { setOpen(false); openBooking(); }}
              className="mt-2 flex items-center justify-center gap-1.5 rounded-full bg-peach-dark px-5 py-3 font-heading text-sm font-bold text-cream"
            >
              Book Strategy Call — ₹199
              <ArrowUpRight size={15} />
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
